import React, { useEffect, useState } from "react";
import { BecomeVendorImage } from "../../../assets/data/Imagedata";
import { IoMdLogIn } from "react-icons/io";
import { useLocation, useNavigate } from "react-router-dom";

function PaymentSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const { orderId, totalAmount } = location.state || {};

  const [countdown, setCountdown] = useState(10);
  const [isValidOrder, setIsValidOrder] = useState(false);

  useEffect(() => {
    console.log("PaymentSuccess mounted with state:", location.state);
    
    // Check if we have valid order data
    if (orderId && totalAmount) {
      setIsValidOrder(true);
      console.log("Valid order data found");
    } else {
      console.log("No valid order data, redirecting...");
      // Try to get from localStorage as fallback
      const storedOrder = localStorage.getItem('lastSuccessfulOrder');
      if (storedOrder) {
        const orderData = JSON.parse(storedOrder);
        navigate("/Store/checkout/paymentsuccess", { 
          state: orderData,
          replace: true 
        });
        return;
      }
      navigate("/user/orders", { replace: true });
      return;
    }

    // Start countdown timer
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate("/user/orders", { replace: true });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [location, navigate, orderId, totalAmount]);

  const handleOrdersPage = () => {
    navigate("/user/orders");
  };

  const handleContinueShopping = () => {
    navigate("/Store");
  };

  const handleGoHome = () => {
    navigate("/");
  };

  if (!isValidOrder) {
    return (
      <section className="flex flex-col items-center justify-center min-h-screen bg-gray-100 px-6 py-12 text-center">
        <div className="max-w-3xl bg-white p-8 rounded-lg shadow-xl">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h1 className="text-2xl font-bold text-gray-900">Loading Order Details...</h1>
          <p className="text-lg text-gray-600 mt-4">Please wait while we retrieve your order information.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-green-50 to-blue-50 px-6 py-12 text-center">
      <div className="max-w-2xl bg-white p-8 rounded-2xl shadow-2xl border border-green-200">
        {/* Success Icon with Animation */}
        <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
          <svg className="w-12 h-12 text-green-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </div>

        <h1 className="text-4xl font-bold text-gray-900 mb-2">
          Payment Successful! 🎉
        </h1>
        <p className="text-xl text-green-600 font-semibold mb-2">Thank you for your purchase!</p>
        <p className="text-lg text-gray-600 mb-6">Your order has been confirmed and will be processed shortly.</p>

        {/* Order Details Card */}
        <div className="mt-6 p-6 bg-gradient-to-r from-blue-50 to-green-50 rounded-xl border border-blue-200">
          <h3 className="text-2xl font-bold text-gray-800 mb-4">Order Confirmation</h3>
          <div className="space-y-3 text-left">
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <strong className="text-gray-700">Order ID:</strong> 
              <span className="font-mono text-blue-600 bg-blue-50 px-3 py-1 rounded-lg">#{orderId}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <strong className="text-gray-700">Total Amount:</strong> 
              <span className="text-green-600 font-bold text-xl">₹{typeof totalAmount === 'number' ? totalAmount.toLocaleString() : totalAmount}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <strong className="text-gray-700">Status:</strong> 
              <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full font-semibold">Confirmed</span>
            </div>
          </div>
        </div>

        <p className="mt-6 text-gray-700 text-lg">
          A confirmation email has been sent to your registered email address with all the order details.
        </p>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
          <button
            onClick={handleOrdersPage}
            className="flex items-center justify-center bg-blue-600 text-white font-semibold py-3 px-8 rounded-lg shadow-lg hover:bg-blue-700 transition duration-300 transform hover:scale-105"
          >
            <IoMdLogIn className="mr-2 text-xl" /> View My Orders
          </button>
          <button
            onClick={handleContinueShopping}
            className="flex items-center justify-center bg-green-600 text-white font-semibold py-3 px-8 rounded-lg shadow-lg hover:bg-green-700 transition duration-300 transform hover:scale-105"
          >
            Continue Shopping
          </button>
          <button
            onClick={handleGoHome}
            className="flex items-center justify-center bg-gray-600 text-white font-semibold py-3 px-8 rounded-lg shadow-lg hover:bg-gray-700 transition duration-300 transform hover:scale-105"
          >
            Go Home
          </button>
        </div>

        {/* Countdown */}
        <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-lg text-yellow-800">
            <span className="font-semibold">Note:</span> You will be automatically redirected to your orders page in 
            <span className="font-bold text-yellow-600 mx-1">{countdown}</span> 
            seconds...
          </p>
        </div>
      </div>

      {/* Thank You Image */}
      <div className="mt-12 max-w-md w-full">
        <img
          src={BecomeVendorImage}
          alt="Thank You for your purchase"
          className="w-full h-auto rounded-2xl shadow-lg transform hover:scale-105 transition duration-500"
        />
        <p className="mt-4 text-gray-600 italic">We appreciate your business and look forward to serving you again!</p>
      </div>
    </section>
  );
}

export default PaymentSuccess;